import { TextInput } from 'cpool-components/input/TextInput';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SchemaCreditRequestForm } from '../../../schemas/creditRequestForm.schema';
import type { RefinanceableLoanType } from '../../../types';
import { FPS } from '~/config.ts';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { checkIBAN } from '~/features/leadDetails/utils/checkIBAN.ts';
import { externalFetch } from '~/features/leadDetails/utils/externalFetch.ts';

type IbanInputProps = {
    name: `${RefinanceableLoanType}.${number}`;
    shouldFetchExternal?: boolean;
    disabled?: boolean;
};

const IbanInput: React.FC<IbanInputProps> = ({ disabled, name, shouldFetchExternal }) => {
    const { t } = useTranslation();
    const { control, setValue, trigger } = useFormContext<SchemaCreditRequestForm>();

    const lookupBankDetails = async (iban: string) => {
        const trimmedIBAN = iban.replace(/ /g, '');
        if (!trimmedIBAN || !checkIBAN(trimmedIBAN)) return;

        const response = await externalFetch(
            FPS.API_HOST,
            `/api/iban/check/${trimmedIBAN}`,
            { method: 'GET' },
            FPS.IBAN_TOKEN,
        );
        if (!response) return;

        const ibanDetails = await response.json();
        if (!ibanDetails || ibanDetails.status !== 'ok') return;

        setValue(`${name}.bic`, ibanDetails.data.bankDetails.bic);
        setValue(`${name}.glaeubiger`, ibanDetails.data.bankDetails.name);
        await trigger(`${name}.bic`);
    };

    return (
        <FormField<SchemaCreditRequestForm, string | null> control={control} name={`${name}.iban`}>
            {({ error, onBlur, onChange, value }) => (
                <TextInput
                    id={`${name}.iban`}
                    label={t('EXISTING_LOANS.LOAN_IBAN')}
                    value={value ?? ''}
                    onBlur={onBlur}
                    onChange={async (event) => {
                        onChange(event.target.value);
                        if (shouldFetchExternal) await lookupBankDetails(event.target.value);
                    }}
                    disabled={disabled}
                    labelClassName="h-8 w-[175px] shrink-0 justify-start"
                    error={error}
                />
            )}
        </FormField>
    );
};

export default IbanInput;
